import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";
import {
  colorsFoundation,
  borderRadius,
  dimensions,
  colorsSemantic,
} from "@accredo/fe-shared/theme/index.js";
import typography from "@accredo/fe-shared/theme/tokens/typography.js";
import ClaimStatusBadge from "./ClaimStatusBadge.jsx";

/**
 * ObjectInfoCard Component
 * Displays object information with status and actions
 */
export default function ObjectInfoCard({
  object,
  contract,
  branch,
  status = { label: "gespeichert", value: "saved" },
  onEditBranch,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleEditClick = () => {
    setDialogOpen(true);
  };

  const handleConfirmEdit = () => {
    setDialogOpen(false);
    if (onEditBranch) {
      onEditBranch();
    }
  };

  const handleCancelEdit = () => {
    setDialogOpen(false);
  };

  // Build object title from available data
  const getObjectTitle = () => {
    if (object?.bezeichnung) {
      return object.bezeichnung;
    }
    if (object?.strasse) {
      let title = object.strasse;
      if (object.hausnr) title += " " + object.hausnr;
      return title;
    }

    return "Objekt";
  };

  // Build object subtitle
  const getBranchInfo = () => {
    const parts = [];

    if (branch) {
      parts.push(branch);
    }

    return parts.join(" • ");
  };

  const getContractInfo = () => {
    // console.log({ contract, object });
    let info = [];

    if (object.typ) {
      info.push(object.typ);
    }
    if (contract?.vertragsart) {
      info.push(contract.vertragsart);
    }

    if (contract?.vertragsnummer) {
      info.push(contract.vertragsnummer);
    }

    if (contract?.gesellschaft_name) {
      info.push(contract.gesellschaft_name);
    }

    return info.join(" | ");
  };

  return (
    <Box
      sx={{
        backgroundColor: colorsFoundation.neutral[50],
        borderRadius: borderRadius.sm,
        padding: dimensions["2xl"],
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Left side: Object info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: typography.weight.semibold,
              fontSize: typography.size.xl,
              mb: 1,
            }}
          >
            {getObjectTitle()}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "auto 1fr", sm: "auto 1fr" },
              rowGap: 0.5,
              columnGap: 2,
              alignItems: "center",
            }}
          >
            {/* Vertrag row */}
            {getContractInfo() && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorsFoundation.neutral[700],
                    fontWeight: typography.weight.semibold,
                  }}
                >
                  Vertrag
                </Typography>
                <Typography variant="body2">{getContractInfo()}</Typography>
              </>
            )}

            {/* Wirtschaftseinheit row */}
            {contract?.kunde?.object_ref_num_label && object?.customer_reference_number && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorsFoundation.neutral[700],
                    fontWeight: typography.weight.semibold,
                  }}
                >
                  {contract.kunde.object_ref_num_label}
                </Typography>
                <Typography variant="body2">
                  {object.customer_reference_number}
                </Typography>
              </>
            )}

            {/* Sparte row */}
            {getBranchInfo() && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorsFoundation.neutral[700],
                    fontWeight: typography.weight.semibold,
                  }}
                >
                  Sparte
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">{getBranchInfo()}</Typography>
                  {onEditBranch && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleEditClick}
                      sx={{
                        color: colorsSemantic.text.subtle,
                        textTransform: "none",
                        border: "none",
                        borderBottom: `1px solid transparent`,
                        borderRadius: 0,
                        fontSize: typography.size.sm,
                        fontWeight: typography.weight.regular,
                        py: 0.25,
                        px: 1,
                        "& .MuiButton-startIcon > *:nth-of-type(1)": {
                          fontSize: typography.size.sm,
                        },
                        // bottom border on hover
                        "&:hover": {
                          borderBottom: `1px solid ${colorsFoundation.key.blue[700]}`,
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      ändern
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Right side: Status badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colorsFoundation.neutral[600],
              fontWeight: 500,
            }}
          >
            Status
          </Typography>
          <ClaimStatusBadge status={status.value} label={status.label} />
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelEdit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Sparte ändern?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Wenn Sie die Sparte ändern, gehen alle bisher eingegebenen Daten im
            Formular verloren. Möchten Sie fortfahren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} color="primary">
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmEdit}
            color="primary"
            variant="contained"
            autoFocus
          >
            Fortfahren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
